import { Button } from '@/components/ui/button';
import { Github } from 'lucide-react';
import { useOAuthConfig } from '@/hooks/use-oauth-config';
import { api } from '@/lib/api';
import { useState } from 'react';
// import { useToast } from '@/components/ui/use-toast';
const useToast = () => ({ toast: (msg: any) => console.log(msg) }); // Temporary mock

// Custom Google icon since lucide-react doesn't have one
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

interface OAuthButtonsProps {
  mode?: 'login' | 'register';
}

export function OAuthButtons({ mode = 'login' }: OAuthButtonsProps) {
  const { data: config, isLoading } = useOAuthConfig();
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const { toast } = useToast();

  const handleOAuthLogin = async (provider: 'github' | 'google') => {
    try {
      setLoadingProvider(provider);

      // Get OAuth login URL from backend
      const response = await api.get(`/auth/${provider}/login`);

      // Store state for CSRF protection (optional)
      if (response.data.state) {
        sessionStorage.setItem('oauth_state', response.data.state);
      }

      // Redirect to OAuth provider
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      console.error('OAuth initialization failed:', error);
      toast({
        title: 'OAuth Error',
        description: `Failed to initialize ${provider} login. Please try again.`,
        variant: 'destructive',
      });
      setLoadingProvider(null);
    }
  };

  // Don't show anything while loading or if no providers are configured
  if (isLoading || !config || (!config.providers || config.providers.length === 0)) {
    return null;
  }

  const actionText = mode === 'register' ? 'Sign up' : 'Sign in';

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>

      <div className="grid gap-2">
        {config.github_enabled && (
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOAuthLogin('github')}
            disabled={loadingProvider !== null}
            className="w-full"
          >
            {loadingProvider === 'github' ? (
              <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <Github className="mr-2 h-4 w-4" />
            )}
            {actionText} with GitHub
          </Button>
        )}
        
        {config.google_enabled && (
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOAuthLogin('google')}
            disabled={loadingProvider !== null}
            className="w-full"
          >
            {loadingProvider === 'google' ? (
              <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <GoogleIcon className="mr-2 h-4 w-4" />
            )}
            {actionText} with Google
          </Button>
        )}
      </div>

      {mode === 'register' && (
        <p className="text-xs text-center text-muted-foreground">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      )}
    </div>
  );
}